import {
  ACCESS_REQUEST_ERROR_CODES,
  ACCESS_REQUEST_FIELD_LIMITS,
  getAccessRequestSubmitErrorMessage,
  parseScopeListInput,
  validateScopeText,
  type AccessScopeType,
} from "@/lib/api/access-request-form";
import type {
  ApiPlatform,
  CreateIntegratorRequestPayload,
  KeyEnvironment,
  RequesterType,
} from "@/lib/api/access-requests";
import { parseApiError } from "@/lib/api/api-error";

export const INTEGRATOR_REQUEST_FIELD_LIMITS = {
  integratorNameMin: 3,
  integratorNameMax: 255,
} as const;

export const INTEGRATOR_ENVIRONMENT_OPTIONS: {
  value: KeyEnvironment;
  label: string;
}[] = [
  { value: "sandbox", label: "Sandbox" },
  { value: "live", label: "Live" },
];

export interface IntegratorRequestFormValues {
  integratorName: string;
  environment: KeyEnvironment;
  accessScopeType: AccessScopeType;
  namespacesText: string;
  locationNamesText: string;
  intendedUseCase: string;
}

export function createInitialIntegratorRequestForm(
  environment: KeyEnvironment = "sandbox",
): IntegratorRequestFormValues {
  return {
    integratorName: "",
    environment,
    accessScopeType: "namespaces",
    namespacesText: "",
    locationNamesText: "",
    intendedUseCase: "",
  };
}

export function getIntegratorScopeText(
  form: IntegratorRequestFormValues,
): string {
  return form.accessScopeType === "namespaces"
    ? form.namespacesText
    : form.locationNamesText;
}

export function isIntegratorRequestFormValid(
  form: IntegratorRequestFormValues,
): boolean {
  const name = form.integratorName.trim();
  if (
    name.length < INTEGRATOR_REQUEST_FIELD_LIMITS.integratorNameMin ||
    name.length > INTEGRATOR_REQUEST_FIELD_LIMITS.integratorNameMax
  ) {
    return false;
  }

  const useCase = form.intendedUseCase.trim();
  if (
    useCase.length < ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMin ||
    useCase.length > ACCESS_REQUEST_FIELD_LIMITS.intendedUseCaseMax
  ) {
    return false;
  }

  if (form.environment === "sandbox") {
    return true;
  }

  const scopeText = getIntegratorScopeText(form);
  if (validateScopeText(scopeText)) {
    return false;
  }
  return parseScopeListInput(scopeText).length > 0;
}

/**
 * Sandbox requests must omit every scope field: the backend rejects a scope
 * with `accessrequests.scope_not_allowed_for_sandbox`.
 */
export function toCreateIntegratorRequestPayload(
  form: IntegratorRequestFormValues,
  apiPlatform: ApiPlatform,
  requesterType?: RequesterType,
): CreateIntegratorRequestPayload {
  const payload: CreateIntegratorRequestPayload = {
    api_platform: apiPlatform,
    integrator_name: form.integratorName.trim(),
    environment: form.environment,
    ...(requesterType ? { requester_type: requesterType } : {}),
    intended_use_case: form.intendedUseCase.trim(),
  };

  if (form.environment !== "live") {
    return payload;
  }

  const values = parseScopeListInput(getIntegratorScopeText(form));
  payload.access_scope = form.accessScopeType;
  if (form.accessScopeType === "namespaces") {
    payload.namespaces = values;
  } else {
    payload.location_names = values;
  }

  return payload;
}

const INTEGRATOR_SUBMIT_MESSAGES = {
  duplicate: "You already have an integrator request for this environment.",
  scopeNotAllowedForSandbox:
    "Sandbox requests cannot include an access scope. Switch to Live to request scoped access.",
  locationNamesRequired:
    "Enter at least one location name for a Live request.",
} as const;

export function getIntegratorRequestSubmitErrorMessage(err: unknown): string {
  const { status, code } = parseApiError(err);

  if (status === 409 && code === ACCESS_REQUEST_ERROR_CODES.duplicateRequest) {
    return INTEGRATOR_SUBMIT_MESSAGES.duplicate;
  }

  if (status === 400) {
    if (code === ACCESS_REQUEST_ERROR_CODES.scopeNotAllowedForSandbox) {
      return INTEGRATOR_SUBMIT_MESSAGES.scopeNotAllowedForSandbox;
    }
    if (code === ACCESS_REQUEST_ERROR_CODES.locationIdsNotSupported) {
      return INTEGRATOR_SUBMIT_MESSAGES.locationNamesRequired;
    }
  }

  return getAccessRequestSubmitErrorMessage(err);
}
